import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setMoveStage, setRotateStage } from 'store/slices/ToolSlice';
import { ThemeProvider } from 'contexts/ThemeContext';
import { CustomToastContainer } from 'components/Basic/CustomToast';
import BraveWarningModal from 'components/Basic/Modal/BraveWarningModal';

const queryClient = new QueryClient();
const BRAVE_WARNING = 'This tool may not work as expected in Brave Browser. For the best experience, please switch to another browser.';

function App() {
	const dispatch = useDispatch<AppDispatch>();
	const [showBraveWarning, setShowBraveWarning] = useState(false);
	const debugDialogOpen = useSelector((state: RootState) => state.debugDialog.data.open);

	useEffect(() => {
		const handleKeyPress = (event: KeyboardEvent) => {
			// Shift joining an already-held Ctrl/Cmd switches pan → guide,
			// so pan mode must drop or drawing stays disabled.
			if (event.key === 'Shift' && (event.ctrlKey || event.metaKey)) {
				dispatch(setMoveStage({ shouldMoveStage: false }));
				return;
			}
			if (event.key === 'Alt') {
				// Alt alone can shift focus to the browser's menu bar in some browsers (e.g. Firefox) — suppress that.
				event.preventDefault();
				dispatch(setRotateStage({ shouldRotateStage: true }));
				// Move and Rotate are mutually exclusive nav modes on the stage.
				dispatch(setMoveStage({ shouldMoveStage: false }));
				return;
			}
			if (event.key !== 'Control' && event.key !== 'Meta') return;
			const enablingMove = !event.shiftKey;
			dispatch(setMoveStage({ shouldMoveStage: enablingMove }));
			if (enablingMove) dispatch(setRotateStage({ shouldRotateStage: false }));
		};

		const handleKeyUp = () => {
			dispatch(setMoveStage({ shouldMoveStage: false }));
			dispatch(setRotateStage({ shouldRotateStage: false }));
		};

		// Alt-tab / window-switch swallows the keyup, which would otherwise leave rotate mode stuck on.
		const handleBlur = () => {
			dispatch(setMoveStage({ shouldMoveStage: false }));
			dispatch(setRotateStage({ shouldRotateStage: false }));
		};

		window.addEventListener('keydown', handleKeyPress);
		window.addEventListener('keyup', handleKeyUp);
		window.addEventListener('blur', handleBlur);
		return () => {
			window.removeEventListener('keydown', handleKeyPress);
			window.removeEventListener('keyup', handleKeyUp);
			window.removeEventListener('blur', handleBlur);
		};
	}, [dispatch]);

	useEffect(() => {
		// Single source of truth for blocking shortcuts while the debug-report popup is open:
		// every shortcut listener in the app (this file included) attaches on window/document
		// in the bubble phase, so capturing the event first and stopping its propagation here
		// keeps it from ever reaching any of them - no per-shortcut check needed. Escape is
		// left alone so the popup itself (reactjs-popup's own document-level listener) can
		// still close on it.
		if (!debugDialogOpen) return;
		const blockShortcuts = (event: KeyboardEvent) => {
			if (event.key === 'Escape') return;
			event.stopPropagation();
		};
		window.addEventListener('keydown', blockShortcuts, { capture: true });
		window.addEventListener('keyup', blockShortcuts, { capture: true });
		return () => {
			window.removeEventListener('keydown', blockShortcuts, { capture: true });
			window.removeEventListener('keyup', blockShortcuts, { capture: true });
		};
	}, [debugDialogOpen]);

	useEffect(() => {
		let fired = false;

		const handleFullViewMessage = (event: MessageEvent) => {
			if (fired) return;
			if (event.data?.id !== 'FULL_VIEW_MODE') return;

			fired = true;

			const detectBraveBrowser = async () => {
				try {
					const nav = window.navigator as any;
					if (nav.brave && typeof nav.brave.isBrave === 'function') {
						const isBrave = await nav.brave.isBrave();
						if (isBrave) setShowBraveWarning(true);
						return;
					}
					if (/Brave/i.test(window.navigator.userAgent)) {
						setShowBraveWarning(true);
					}
				} catch {
					// ignore browser detection failures
				}
			};

			detectBraveBrowser();
		};

		window.addEventListener('message', handleFullViewMessage);
		return () => window.removeEventListener('message', handleFullViewMessage);
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>
				<Router>
					<div className='bodyWrapper'>
						<CustomToastContainer containerId={'APP_TOAST'} />
						<AppRoutes />
					</div>
					<BraveWarningModal
						open={showBraveWarning}
						onClose={() => setShowBraveWarning(false)}
						message={BRAVE_WARNING}
					/>
				</Router>
			</ThemeProvider>
		</QueryClientProvider>
	);
}

export default App;
